import { Check, ChevronRight, UserPlus, FileText, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { cn } from '@/lib/utils';
import type { Business } from '@/lib/api';

interface OnboardingStep {
  id: string;
  title: string;
  subtitle: string;
  completed: boolean;
  icon: React.ReactNode;
  color: string;
  action?: { label: string; href: string };
}

export function OnboardingChecklist({ business }: { business: Business | null }) {
  const { nav } = useBusinessNav();
  const onboarding = business?.onboarding;

  if (!onboarding) return null;

  const allComplete =
    onboarding.has_clients && onboarding.has_documents && onboarding.has_sent_document;
  if (allComplete) return null;

  const steps: OnboardingStep[] = [
    {
      id: 'client',
      title: 'Add your first client',
      subtitle: "You'll need a client before you can send anything",
      completed: onboarding.has_clients,
      icon: <UserPlus className="size-4" />,
      color: 'blue',
      action: !onboarding.has_clients
        ? { label: 'Add a client', href: '/clients?new=true' }
        : undefined,
    },
    {
      id: 'document',
      title: 'Create a quote or invoice',
      subtitle: 'Put together your first document',
      completed: onboarding.has_documents,
      icon: <FileText className="size-4" />,
      color: 'violet',
      action: !onboarding.has_documents
        ? { label: 'Create an invoice', href: '/invoices/create' }
        : undefined,
    },
    {
      id: 'send',
      title: 'Send it to your client',
      subtitle: 'Email or text it in one tap',
      completed: onboarding.has_sent_document,
      icon: <Send className="size-3.5" />,
      color: 'emerald',
      action:
        !onboarding.has_sent_document && onboarding.has_documents
          ? {
              label: 'View your drafts',
              href: onboarding.has_invoices ? '/invoices?status=draft' : '/quotes?status=draft',
            }
          : undefined,
    },
  ];

  const firstIncompleteIdx = steps.findIndex((s) => !s.completed);

  const colorMap: Record<string, { ring: string; bg: string; text: string; pulse: string; button: string }> = {
    blue: {
      ring: 'ring-blue-500/30',
      bg: 'bg-blue-500',
      text: 'text-blue-600',
      pulse: 'bg-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    violet: {
      ring: 'ring-violet-500/30',
      bg: 'bg-violet-500',
      text: 'text-violet-600',
      pulse: 'bg-violet-400',
      button: 'bg-violet-600 hover:bg-violet-700',
    },
    emerald: {
      ring: 'ring-emerald-500/30',
      bg: 'bg-emerald-500',
      text: 'text-emerald-600',
      pulse: 'bg-emerald-400',
      button: 'bg-emerald-600 hover:bg-emerald-700',
    },
  };

  return (
    <Card className="mb-6 overflow-hidden border-primary/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Get started with InvoiceOwl</CardTitle>
        <p className="text-sm text-muted-foreground">
          You're a few steps away from sending your first invoice.
        </p>
      </CardHeader>
      <CardContent className="pb-3">
        <div className="space-y-1">
          {steps.map((step, idx) => {
            const isCurrentStep = idx === firstIncompleteIdx;
            const isFutureStep = !step.completed && idx > firstIncompleteIdx;
            const colors = colorMap[step.color];

            return (
              <button
                key={step.id}
                onClick={() => step.action && nav(step.action.href)}
                disabled={!step.action}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 min-h-12 text-left transition-all rounded-lg',
                  isCurrentStep && 'bg-muted/40 ring-1 ring-inset ring-primary/10',
                  isFutureStep && 'opacity-50',
                  step.action && 'hover:bg-muted/60 cursor-pointer',
                )}
              >
                {/* Circle */}
                <div className="relative shrink-0">
                  {isCurrentStep && (
                    <div className={cn('absolute inset-0 rounded-full animate-ping opacity-20', colors.pulse)} />
                  )}
                  <div
                    className={cn(
                      'relative size-8 rounded-full flex items-center justify-center shrink-0 transition-all',
                      step.completed
                        ? 'bg-emerald-500 text-white'
                        : isCurrentStep
                          ? cn(colors.bg, 'text-white ring-4', colors.ring)
                          : 'border-2 border-muted-foreground/20 text-muted-foreground/40'
                    )}
                  >
                    {step.completed ? <Check className="size-4" strokeWidth={3} /> : step.icon}
                  </div>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      step.completed && 'text-muted-foreground line-through',
                      isCurrentStep && 'text-foreground'
                    )}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{step.subtitle}</p>
                </div>

                {/* CTA */}
                {isCurrentStep && step.action && (
                  <span className={cn(
                    'shrink-0 text-xs font-semibold text-white px-3.5 py-1.5 rounded-full transition-colors',
                    colors.button
                  )}>
                    {step.action.label}
                  </span>
                )}
                {!isCurrentStep && step.action && (
                  <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
